import React, { useState, useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { useStyle } from '../../style'
import {
  useConversationContext,
  type BaseMessage,
} from '../../../../stores/conversation.store'
import BasePage from '../../../components/BasePage'
import { getChat } from '../../../../api/chat'

import {
  decoder,
  mapStoredMessagesToUIMessages,
  scrollToBottom,
} from '../../../../utils'
import { useModelConfigContext } from '../../../../stores/modelConfig.store'
import type { ChatConversationViewProps } from '../../types'
import ResponseBubble from '../../../components/ResponseBubble'
import RequestBubble from '../../../components/RequestBubble'
import { useThrottle } from '../../../../hooks/useThrottle'
import ChatInput from '../input'
import ModelSelect from '../../../components/ModelSelect'

interface ChatUiMessage extends BaseMessage {
  role: 'USER' | 'ASSISTANT'
  content: string
  createTime: string
  messageId: string
  isError: boolean
  thinkContent?: string
  medium?: any
}

const ChatConversationView = ({
  conversationId,
}: ChatConversationViewProps) => {
  const { styles } = useStyle()
  const location = useLocation()
  const [inputContent, setInputContent] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isHeader, setIsHeader] = useState(false)
  const [historyMessages, setHistoryMessages] = useState<any[]>([])
  const messagesContainerRef = useRef<HTMLDivElement>(null)

  const { currentModel } = useModelConfigContext()

  const {
    activeConversation,
    chooseActiveConversation,
    aiCapabilities,
    appendAssistantMessage,
    processSendMessage,
    processReloadMessage,
    deleteMessageAndAfter,
    updateMessageContent,
    updateActiveConversation,
    fetchConversations,
  } = useConversationContext()

  // 跟踪组件是否首次加载，用于处理URL中的prompt参数
  const isFirstLoad = useRef(true)
  const processedPrompts = useRef(new Set<string>())

  useEffect(() => {
    chooseActiveConversation(conversationId)
  }, [conversationId])

  useEffect(() => {
    scrollToBottom(messagesContainerRef.current)
  }, [historyMessages])

  // 将会话渲染为消息列表
  useEffect(() => {
    if (!activeConversation) {
      setHistoryMessages([])
      return
    }
    const messages = activeConversation.messages ?? []
    // const filteredMessages = messages.filter((msg: any) => !msg.isError)
    setHistoryMessages(
      mapStoredMessagesToUIMessages(messages as ChatUiMessage[])
    )
  }, [activeConversation?.conversationId, activeConversation?.messages])

  // 处理URL中的prompt参数
  useEffect(() => {
    if (isFirstLoad.current && activeConversation) {
      const queryParams = new URLSearchParams(location.search)
      const urlPrompt = queryParams.get('prompt')
      const urlImageUrls = queryParams.get('imageUrls')
      const urlDocUrls = queryParams.get('docUrls')

      let imageUrls: string[] = []
      let docUrls: any[] = []

      try {
        if (urlImageUrls) {
          imageUrls = JSON.parse(urlImageUrls)
        }
        if (urlDocUrls) {
          docUrls = JSON.parse(urlDocUrls)
        }
      } catch (error) {
        console.warn('解析URL参数失败:', error)
      }

      if (urlPrompt && !processedPrompts.current.has(urlPrompt)) {
        // 标记此prompt已处理，避免重复处理
        processedPrompts.current.add(urlPrompt)
        // 清除URL中的prompt参数，防止刷新页面重复发送
        const newUrl = window.location.hash.split('?')[0]
        window.history.replaceState({}, document.title, newUrl)
        if (activeConversation && !isLoading) {
          handleSendMessage(urlPrompt, imageUrls, docUrls)
        }
        const timeId = setTimeout(() => {
          fetchConversations()
          clearTimeout(timeId)
        }, 3000)
      }

      isFirstLoad.current = false
    }
  }, [location.search, activeConversation, isLoading])

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 1920) setIsHeader(true)
      else setIsHeader(false)
    }

    handleResize()
    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  const updateConversationMessages =
    //  useThrottle(
    (
      assistantContent: any,
      role: 'ASSISTANT',
      isError: boolean = false,
      userTimestamp: string,
      userMessage: ChatUiMessage,
      finalMessages: ChatUiMessage[]
    ) => {
      appendAssistantMessage(
        assistantContent,
        role,
        isError,
        userTimestamp,
        userMessage,
        finalMessages
      )
    }
  //   5
  // )

  // 发送消息到API并更新会话
  const handleSendMessage = async (text: string, imageUrls: string[], docUrls: any[]) => {
    if (!text.trim() || isLoading || !activeConversation) return

    const createMessage = (text: string, timestamp: string, imageUrls: string[], docUrls: any[]): ChatUiMessage => ({
      role: 'USER',
      content: text,
      createTime: timestamp,
      messageId: timestamp,
      isError: false,
      medium: {
        images: imageUrls.map((url) => ({ url })),
        docs: docUrls
      }
    })

    const sendRequest = async (
      text: string,
      userTimestamp: string,
      userMessage: ChatUiMessage,
      finalMessages: ChatUiMessage[]
    ) => {
      await getChat(
        (data: any) => {
          updateConversationMessages(
            data,
            'ASSISTANT',
            false,
            userTimestamp,
            userMessage,
            finalMessages
          )
        },
        {
          conversationId: activeConversation?.conversationId,
          model: currentModel || 'dashscope',
          prompt: text,
          conversationType: 'chat',
          enableSearch: aiCapabilities.onlineSearch,
          enableThinking: aiCapabilities.deepThink,
          enableRag: true,
          enableExpandQuery: false,
          enableFunctionCalling: false,
          enableMcp: false,
          imageUrls,
          docUrls
        }
      )
    }

    await processSendMessage({
      text,
      imageUrls,
      docUrls,
      sendRequest,
      createMessage,
      setLoading: setIsLoading,
      setInputContent,
    })
  }

  // 处理重新生成消息（revert操作）
  const handleReloadMessage = async (messageId: string, imageUrls: string[], docUrls: any[]) => {
    if (!activeConversation || isLoading) return

    const updateConversationMessagesWithBase =
      // useThrottle(
      (
        assistantContent: any,
        role: 'ASSISTANT',
        isError: boolean = false,
        messageId: string,
        userMessage: ChatUiMessage,
        finalMessages: ChatUiMessage[]
      ) => {
        appendAssistantMessage(
          assistantContent,
          role,
          isError,
          messageId,
          userMessage,
          finalMessages as ChatUiMessage[]
        )
      }
    //   5
    // )

    const sendRequest = async (
      text: string,
      messageId: string,
      userMessage: ChatUiMessage,
      finalMessages: ChatUiMessage[]
    ) => {
      await getChat(
        (data: any) => {
          updateConversationMessagesWithBase(
            data,
            'ASSISTANT',
            false,
            messageId,
            userMessage,
            finalMessages
          )
        },
        {
          conversationId: activeConversation?.conversationId,
          model: currentModel || 'dashscope',
          prompt: text,
          conversationType: 'chat',
          enableSearch: aiCapabilities.onlineSearch,
          enableThinking: aiCapabilities.deepThink,
          enableRag: true,
          enableExpandQuery: false,
          enableFunctionCalling: false,
          enableMcp: false,
          imageUrls,
          docUrls
        }
      )
    }

    await processReloadMessage({
      messageId,
      sendRequest,
      setLoading: setIsLoading,
      setInputContent,
    })
  }


  return (
    <BasePage title="对话" conversationId={conversationId}>
      <div className={styles.container}>
        {isHeader ? (
          <header className={styles.landingHeader}>
            <ModelSelect />
          </header>
        ) : (
          <div style={{ position: 'absolute', top: 10, left: '1%' }}>
            <ModelSelect />
          </div>
        )}
        <div className={styles.messagesContainer}>
          <div className={styles.article} ref={messagesContainerRef}>
            {historyMessages.map((message) =>
              message.sender === 'USER' ? (
                <RequestBubble key={message.id} content={message.text} images={message.images} docs={message.docs} />
              ) : (
                <ResponseBubble
                  key={message.id}
                  thinkContent={message.thinkContent}
                  content={message.text}
                  onlineSearchResults={message.onlineSearchResults}
                  isError={message.isError}
                  isFinished={message.isFinished}
                  onReload={() => handleReloadMessage(message.id, message.images, message.docs)}
                />
              )
            )}
          </div>
        </div>

        <ChatInput
          inputValue={inputContent}
          onInputChange={setInputContent}
          onSubmit={handleSendMessage}
          isLoading={isLoading}
          isActionButton={true}
        />
      </div>
    </BasePage>
  )
}

export default ChatConversationView
